import { Fragment, useCallback, useState, useEffect } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { Lot } from "../../../../types";

interface SelectLotRangeDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  allLots: Lot[];
  onSelectRange: (lots: Lot[]) => void;
}

export const SelectLotRangeDialog = (props: SelectLotRangeDialogProps) => {
  const { open, setOpen, allLots, onSelectRange } = props;
  const [startLot, setStartLot] = useState<number | "">("");
  const [endLot, setEndLot] = useState<number | "">("");

  // Get available lot numbers for validation
  const availableLotNumbers = allLots
    .map(lot => lot.lotNumber)
    .filter((lotNumber): lotNumber is string => lotNumber !== null && lotNumber !== undefined)
    .map(Number)
    .filter(num => !isNaN(num))
    .sort((a, b) => a - b);
  const minLotNumber = Math.min(...availableLotNumbers);
  const maxLotNumber = Math.max(...availableLotNumbers);

  // Calculate preview of lots that would be selected
  const previewLots = useCallback(() => {
    if (startLot === "" || endLot === "") return [];
    
    const start = Math.min(Number(startLot), Number(endLot));
    const end = Math.max(Number(startLot), Number(endLot));
    
    return allLots.filter(lot => {
      const lotNum = lot.lotNumber ? Number(lot.lotNumber) : null;
      return lotNum !== null && !isNaN(lotNum) && lotNum >= start && lotNum <= end;
    });
  }, [startLot, endLot, allLots]);

  const previewCount = previewLots().length;

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setStartLot("");
      setEndLot("");
    }
  }, [open]);

  const handleSelectRange = () => {
    const lotsToSelect = previewLots();
    onSelectRange(lotsToSelect);
    setOpen(false);
  };

  const isValidRange = () => {
    if (startLot === "" || endLot === "") return false;
    return previewCount > 0;
  };

  return (
    <Transition show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[60]"
        onClose={() => setOpen(false)}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-martEye-700 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white text-left text-martEye-700 shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <div className="px-4 pb-4 pt-5 sm:p-6">
                  <div className="flex items-start justify-between">
                    <DialogTitle
                      as="h3"
                      className="inline-flex items-center gap-x-1.5 text-xl font-bold"
                    >
                      Select Lot Range
                    </DialogTitle>

                    <div className="ml-3 flex h-7 items-center">
                      <button
                        type="button"
                        className="rounded-full bg-martEye-500 p-1 text-martEye-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-martEye-400 hover:bg-martEye-400"
                        onClick={() => setOpen(false)}
                      >
                        <span className="sr-only">Close panel</span>
                        <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 space-y-4">
                    <p className="text-sm text-gray-600">
                      Select a range of lots by entering the start and end lot numbers.
                      Available lots: {minLotNumber} - {maxLotNumber}
                    </p>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label
                          htmlFor="startLot"
                          className="block text-sm font-medium text-gray-900 mb-1"
                        >
                          Start Lot
                        </label>
                        <input
                          id="startLot"
                          name="startLot"
                          type="number"
                          min={minLotNumber}
                          max={maxLotNumber}
                          value={startLot}
                          onChange={(e) => {
                            const value = e.target.value === "" ? "" : parseInt(e.target.value);
                            if (value !== "" && value > maxLotNumber) {
                              setStartLot(maxLotNumber);
                            } else {
                              setStartLot(value);
                            }
                          }}
                          className="block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:border-martEye-500 focus:outline-none focus:ring-1 focus:ring-martEye-500"
                          placeholder="e.g. 1"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="endLot"
                          className="block text-sm font-medium text-gray-900 mb-1"
                        >
                          End Lot
                        </label>
                        <input
                          id="endLot"
                          name="endLot"
                          type="number"
                          min={minLotNumber}
                          max={maxLotNumber}
                          value={endLot}
                          onChange={(e) => {
                            const value = e.target.value === "" ? "" : parseInt(e.target.value);
                            if (value !== "" && value > maxLotNumber) {
                              setEndLot(maxLotNumber);
                            } else {
                              setEndLot(value);
                            }
                          }}
                          className="block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:border-martEye-500 focus:outline-none focus:ring-1 focus:ring-martEye-500"
                          placeholder="e.g. 10"
                        />
                      </div>
                    </div>

                    {isValidRange() && (
                      <div className="bg-martEye-50 border border-martEye-200 rounded-md p-3">
                        <p className="text-sm text-martEye-700">
                          {previewCount} lot{previewCount !== 1 ? 's' : ''} will be selected
                        </p>
                      </div>
                    )}

                    {startLot !== "" && endLot !== "" && previewCount === 0 && (
                      <div className="bg-red-50 border border-red-200 rounded-md p-3">
                        <p className="text-sm text-red-700">
                          No lots found in the specified range. Please check the lot numbers.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 gap-x-1.5">
                  <button
                    type="button"
                    onClick={handleSelectRange}
                    disabled={!isValidRange()}
                    className="inline-flex w-full justify-center rounded-md bg-martEye-400 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-martEye-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-martEye-400 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto"
                  >
                    OK
                  </button>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-5 py-3 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  >
                    Cancel
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SelectLotRangeDialog;